import React from 'react';
import { markComponentStart } from 'src/commons/utils/logWithTakt';
import { Components } from 'src/commons/constants';
import { performanceMark } from 'src/utils/performance-helper';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { store } from 'src/app/store';
import { ChromeConfigContextProvider } from 'src/providers/ChromeConfigContextProvider';
import { ConnectedChromeApp } from 'src/commons/components/ChromeApp/ChromeApp';
import { ConnectedPivotNav } from 'src/features/pivotNav/PivotNav';
import { PivotNavBundleProvider } from 'src/providers/PantherLocalizationProvider/PantherLocalizationProvider';
import { InternalChromeConfigContextProvider } from 'src/providers/InternalChromeConfigContextProvider';
import AACChromeAPI from '@amzn/aac-chrome-api';
import {
  PAGE_LAYOUT_TYPE,
  PivotNav,
  updateChromeConfiguration,
} from 'src/app/slice';
import { isMobileForTrim } from 'src/commons/utils/helpers';
import { ErrorIdProvider } from '@amzn/storm-ui';

const PIVOT_NAV_CONFIG = 'initConfig';
export default class AXChromePivotNav extends HTMLElement {
  _initialStyleAdded = false;
  static observedAttributes = [PIVOT_NAV_CONFIG];

  set initConfig(initChromePivotNavConfig: PivotNav) {
    if (
      initChromePivotNavConfig &&
      Object.keys(initChromePivotNavConfig).length > 0
    ) {
      if (!this._initialStyleAdded) {
        this.setInitialStyle(initChromePivotNavConfig);
      }
      this.updateChromeAPI(initChromePivotNavConfig);
    }
  }

  setInitialStyle(initChromePivotNavConfig: PivotNav) {
    const pageLayoutType = document
      .querySelector('meta[name="a2z:mons_page_layout_type"]')
      ?.getAttribute('content');

    if (pageLayoutType !== PAGE_LAYOUT_TYPE.STANDARD) {
      return;
    }

    const isMobile = isMobileForTrim();
    if (initChromePivotNavConfig?.menuItemGroups ?? [].length > 0) {
      const styleTag = document.createElement('style');
      styleTag.innerHTML = isMobile
        ? `
                ax-chrome-pivot-nav {
                  min-height: 43px;
                }
              `
        : `
              ax-chrome-pivot-nav {
                min-width: 212px;
                min-height: 1px;
              }
            `;
      document.head.appendChild(styleTag);
      this._initialStyleAdded = true;
    }
  }

  updateChromeAPI(initChromePivotNavConfig: PivotNav) {
    const chromeConfig = AACChromeAPI.getChromeConfiguration();
    chromeConfig.setPivotNav(initChromePivotNavConfig);
    store.dispatch(updateChromeConfiguration(chromeConfig));
    AACChromeAPI.setChromeConfiguration(chromeConfig);
  }

  async _renderPivotNav() {
    if (window.__AAC_CHROME_HIDE_WC_NAVS_PROMISE__) {
      const hide = await window.__AAC_CHROME_HIDE_WC_NAVS_PROMISE__;
      if (hide) return;
    }

    markComponentStart(Components.PIVOT_NAV);
    const endMarker = performanceMark('aac-chrome-ui-pivot-nav');
    ReactDOM.render(
      <ErrorIdProvider id="Chrome">
        <Provider store={store}>
          <InternalChromeConfigContextProvider>
            <ChromeConfigContextProvider>
              <ConnectedChromeApp name="PivotNav">
                <PivotNavBundleProvider>
                  <ConnectedPivotNav />
                </PivotNavBundleProvider>
              </ConnectedChromeApp>
            </ChromeConfigContextProvider>
          </InternalChromeConfigContextProvider>
        </Provider>
      </ErrorIdProvider>,
      this,
    );
    endMarker();
  }

  connectedCallback(): void {
    this._renderPivotNav();
  }
}

if (!customElements.get('ax-chrome-pivot-nav')) {
  customElements.define('ax-chrome-pivot-nav', AXChromePivotNav);
}
